import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { WButton } from '@wareclouds/wareclouds-ui';

import useLogout from '../../../../../hooks/useLogout';
import logo from '../../../../../assets/img/inside-logo.svg';
import { onboardingSecondaryContainedButtonSx } from '../../utils';

import env from '@/config/env';

const ONBOARDING_HELP_URL = (env.ONBOARDING_HELP_URL || '').trim() || '#';

const CreateAccountNavbar = () => {
  const { t } = useTranslation(['onboarding', 'button']);
  const doLogout = useLogout({ redirectTo: '/onboarding' });
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    doLogout();
  };

  return (
    <>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 48, px: 2 }}>
          <Box
            component="img"
            src={logo}
            alt="Wareclouds"
            sx={{ height: 30 }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              color="inherit"
              aria-label="help"
              href={ONBOARDING_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <HelpOutlineIcon />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="exit"
              onClick={() => setShowExitConfirm(true)}
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog
        open={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              maxWidth: 400,
              width: '100%',
              position: 'relative',
              mx: 1,
            },
          },
        }}
      >
        <IconButton
          type="button"
          aria-label={t('close', { ns: 'button' })}
          onClick={() => setShowExitConfirm(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            textAlign: 'center',
            pt: 4,
            pb: 1,
            px: 3,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <ExitToAppIcon
            sx={{
              fontSize: 56,
              color: 'grey.500',
              mb: 2,
              display: 'block',
              mx: 'auto',
            }}
          />
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 22,
              mb: 2,
              color: 'text.primary',
              lineHeight: 1.3,
            }}
          >
            {t('complete_profile.navbar.exit_confirm.title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {t('complete_profile.navbar.exit_confirm.message')}
          </Typography>
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
          }}
        >
          <WButton
            label={t('complete_profile.navbar.exit_confirm.confirm')}
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleConfirmExit}
          />
          <WButton
            label={t('cancel', { ns: 'button' })}
            variant="contained"
            fullWidth
            sx={onboardingSecondaryContainedButtonSx}
            onClick={() => setShowExitConfirm(false)}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAccountNavbar;
